import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, ArrowUpRight, BrainCircuit, Building2, CalendarClock, FileText, Send } from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import CalendarKanban from "@/components/dashboard/CalendarKanban";
import { API, authHeaders } from "@/lib/api";
import { useSystemActivity } from "@/hooks/useSystemActivity";

function Tile({ icon: Icon, label, value, hint, testId }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      data-testid={testId}
      className="hm-surface p-5 flex flex-col gap-3">
      <div className="flex items-center gap-2 text-hm-fg-muted">
        <Icon className="h-4 w-4 text-hm-accent" />
        <span className="mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className="text-3xl font-semibold text-hm-fg leading-none">{value}</div>
      {hint && <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">{hint}</div>}
    </motion.div>
  );
}

function TrendingTopics() {
  const [topics, setTopics] = useState(null);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/insights/emerging-topics?window_hours=168`, { headers: authHeaders() });
        setTopics(r.ok ? await r.json() : []);
      } catch {
        setTopics([]);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="hm-surface p-5" data-testid="dashboard-trending">
      <div className="flex items-center gap-2 mb-4">
        <ArrowUpRight className="h-4 w-4 text-hm-accent" />
        <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-muted">trending topics · 7d</span>
      </div>
      {topics === null ? (
        <div className="mono text-[11px] text-hm-fg-subtle">loading…</div>
      ) : topics.length === 0 ? (
        <div className="mono text-[11px] text-hm-fg-subtle">no topic velocity yet — trigger a workflow.</div>
      ) : (
        <div className="space-y-2">
          {topics.slice(0, 6).map((t) => (
            <div key={t.topic_slug} className="flex items-center gap-3 p-2 rounded-md hover:bg-hm-bg-1">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: t.topic_color }} />
              <span className="text-sm text-hm-fg flex-1 truncate">{t.topic_name}</span>
              <span className="mono text-[10px] text-hm-fg-subtle">cur {t.current} / base {t.baseline}</span>
              <span className={"mono text-[11px] " + (t.velocity >= 1 ? "text-hm-accent" : "text-hm-fg-muted")}>
                {t.velocity >= 1 ? "↑" : "↓"} {t.velocity.toFixed(1)}x
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveActivity() {
  const { events } = useSystemActivity();
  return (
    <div className="hm-surface p-5" data-testid="dashboard-live-activity">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-4 w-4 text-hm-accent" />
        <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-muted">live agent activity</span>
      </div>
      {events.length === 0 ? (
        <div className="mono text-[11px] text-hm-fg-subtle">idle</div>
      ) : (
        <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
          {events.slice(0, 12).map((e, i) => (
            <div key={e.id + i} className="flex items-center gap-2 mono text-[11px] text-hm-fg-muted">
              <span className="h-1 w-1 rounded-full bg-hm-accent shrink-0" />
              <span className="truncate">{e.label}</span>
              <span className="ml-auto text-hm-fg-subtle text-[10px]">{new Date(e.ts).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecentDrafts() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/articles?limit=6`, { headers: authHeaders() });
        setItems(r.ok ? (await r.json()).data : []);
      } catch {
        setItems([]);
      }
    })();
  }, []);
  return (
    <div className="hm-surface p-5" data-testid="dashboard-drafts">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="h-4 w-4 text-hm-accent" />
        <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-muted">recent drafts</span>
      </div>
      {items === null ? (
        <div className="mono text-[11px] text-hm-fg-subtle">loading…</div>
      ) : items.length === 0 ? (
        <div className="mono text-[11px] text-hm-fg-subtle">no drafts yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <a key={a.id} href="/articles" className="block p-2 rounded-md hover:bg-hm-bg-1">
              <div className="text-sm text-hm-fg truncate">{a.title_rewritten}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-0.5">{a.region} · {a.status}</div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function BriefPipeline() {
  const [briefs, setBriefs] = useState(null);
  const { nextScheduled: next } = useSystemActivity();
  useEffect(() => {
    (async () => {
      const b = await fetch(`${API}/daily-briefs?limit=6`, { headers: authHeaders() });
      if (b.ok) {
        const bj = await b.json();
        setBriefs(bj.data || []);
      }
    })();
  }, []);
  const counts = { draft: 0, review: 0, approved: 0, sent: 0 };
  for (const b of briefs || []) {
    if (counts[b.status] !== undefined) counts[b.status]++;
  }
  return (
    <div className="hm-surface p-5" data-testid="dashboard-briefs">
      <div className="flex items-center gap-2 mb-4">
        <Send className="h-4 w-4 text-hm-accent" />
        <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-muted">brief pipeline</span>
        <div className="ml-auto flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle" data-testid="dashboard-next-scheduled">
          <CalendarClock className="h-3.5 w-3.5" />
          {next
            ? "next " +
              new Date(next.next_run).toLocaleString(undefined, {
                timeZone: "Asia/Singapore",
                dateStyle: "short",
                timeStyle: "short",
              }) +
              " SGT"
            : "scheduler idle"}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {["draft", "review", "approved", "sent"].map((s) => (
          <div key={s} className="flex-1 flex flex-col items-center gap-2">
            <div className={"h-1.5 w-full rounded-full " + (counts[s] > 0 ? "bg-hm-accent" : "bg-hm-border-strong")} />
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-muted">{s}</div>
            <div className="mono text-[10px] text-hm-fg-subtle">{counts[s]}</div>
          </div>
        ))}
      </div>
      {briefs && briefs.length > 0 && (
        <a href={`/daily-briefs/${briefs[0].id}`} className="mt-5 block hm-surface p-3 hover:border-hm-border-strong">
          <div className="text-sm text-hm-fg">{briefs[0].brief_date}</div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-1">{briefs[0].status} · {briefs[0].article_count} articles</div>
        </a>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { activeRuns } = useSystemActivity();
  const [tiles, setTiles] = useState({ articles24h: 0, companies: 0, memory: 0 });
  useEffect(() => {
    (async () => {
      const [a, c, m] = await Promise.all([
        fetch(`${API}/articles?limit=1&since=${new Date(Date.now() - 86400000).toISOString()}`, { headers: authHeaders() }),
        fetch(`${API}/companies?limit=1`, { headers: authHeaders() }),
        fetch(`${API}/memory/stats`, { headers: authHeaders() }),
      ]);
      const [aj, cj, mj] = await Promise.all([a.json(), c.json(), m.json()]);
      setTiles({ articles24h: aj.meta?.total || 0, companies: cj.meta?.total || 0, memory: mj.total || 0 });
    })();
  }, []);
  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" badge="Live" subtitle="Your OS at a glance. Overnight activity, surging topics, brief pipeline." />
      <div className="mb-4">
        <CalendarKanban />
      </div>
      <div className="mb-4">
        <OnboardingChecklist />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Tile testId="tile-agents" icon={Activity} label="active agents" value={activeRuns} hint="live" />
        <Tile testId="tile-articles" icon={FileText} label="articles · 24h" value={tiles.articles24h} hint="drafts" />
        <Tile testId="tile-companies" icon={Building2} label="companies tracked" value={tiles.companies} hint="in knowledge base" />
        <Tile testId="tile-memory" icon={BrainCircuit} label="memory entries" value={tiles.memory} hint="short + long term" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <TrendingTopics />
        <LiveActivity />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><BriefPipeline /></div>
        <RecentDrafts />
      </div>
    </div>
  );
}
